import { customLog } from '@/helpers';
import { API_BASE_URL } from '@/config/env';

export interface ApiError {
  statusCode: number;
  message: string;
  error: string;
}

export interface AdminProfile {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  department?: string;
  jobTitle?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  department?: string;
  jobTitle?: string;
}

class ApiService {
  // getServices() {
  //   throw new Error('Method not implemented.');
  // }
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    customLog('API Service: Making request to:', url);
    customLog('API Service: Request options:', options);

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    customLog('API Service: Final config:', url, config);

    try {
      customLog('API Service: Sending fetch request...');
      const response = await fetch(url, config);

      customLog('API Service: Response received', response);
      customLog('API Service: Response status:', response.status);
      customLog('API Service: Response ok:', response.ok);
      customLog('API Service: Response headers:', response.headers);

      if (!response.ok) {
        console.error('API Service: Response not ok, status:', response.status);
        let errorData: ApiError;
        try {
          errorData = await response.json();
          console.error('API Service: Error data:', errorData);
        } catch {
          console.error('API Service: Could not parse error response');
          errorData = {
            statusCode: response.status,
            message: `HTTP error! status: ${response.status}`,
            error: 'Unknown error',
          };
        }

        // Handle specific error cases
        if (response.status === 401) {
          console.error(
            'API Service 401 Unauthorized - token may be invalid or expired'
          );
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          console.error(
            'API Service: 403 Forbidden - insufficient permissions'
          );
          throw new Error('Access denied. Insufficient permissions.');
        } else if (response.status === 404) {
          console.error('API Service: 404 Not Found - endpoint not found');
          throw new Error('API endpoint not found.');
        } else if (response.status >= 500) {
          console.error('API Service: Server error');
          throw new Error('Server error. Please try again later.');
        }

        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      console.log('API Service: Parsing response JSON...');
      const result = await response.json();
      console.log('API Service: Response data:', result);
      return result;
    } catch (error) {
      console.error('API Service: Request failed:', error);
      throw error;
    }
  }

  // Add other API methods as needed

  // Profile Management APIs
  async getAdminProfile(token: string): Promise<AdminProfile> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: AdminProfile;
    }>('/admin/profile', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async updateAdminProfile(
    token: string,
    profileData: UpdateProfileRequest
  ): Promise<AdminProfile> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: AdminProfile;
    }>('/admin/profile', {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(profileData),
    });
    return response.data;
  }

  async changePassword(
    token: string,
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>('/admin/change-password', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
    return { message: response.message };
  }
}

export const apiService = new ApiService();
export default apiService;
